import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Text, Content, Form, Row, Col, Grid, FlexboxGrid } from 'rsuite'
import FlowPageLayout from '../../components/layout/FlowPageLayout'
import PortalHeader from '../../components/layout/PortalHeader'
import { convertStringToKey } from '../../common/utils'
import { mapQuestionToComponent } from '../../common/configMapping'
import { FLOWS_ENUM } from '../../common/enums'
import { selectRenewalPermitData } from '../../store/slices/renew/renewSelectors'
import { updatePermitData } from '../../store/slices/renew/renewSlice'
import { selectConfigExpressPropertyDetailsQuestions, selectConfigExpressRenewPropertyDetailsQuestions, selectConfigExpressUpdatePropertyDetailsQuestions } from '../../store/slices/config/configSelectors'

const RenewPropertyDetails = () => {
    const dispatch = useDispatch()
    const form = useRef()

    // Get distinct supplemental questions from all flows
    // when we save we need to pass in all questions as part of the payload, otherwise they are deleted
    // on this page we only want to display the renew related supplemental questions
    const applyPropertyDetailsQuestions = useSelector(selectConfigExpressPropertyDetailsQuestions);
    const updatePropertyDetailsQuestions = useSelector(selectConfigExpressUpdatePropertyDetailsQuestions);
    const renewPropertyDetailsQuestions = useSelector(selectConfigExpressRenewPropertyDetailsQuestions);

    const allPropertyDetailsQuestions = useMemo(() => {
        const allQuestions = [
            ...(applyPropertyDetailsQuestions || []),
            ...(updatePropertyDetailsQuestions || []),
            ...(renewPropertyDetailsQuestions || [])
        ];

        return allQuestions.reduce((acc, current) => {
            const existingIndex = acc.findIndex(item => item.key === current.key);
            // If key does not exist add it else replace existing value
            if (existingIndex === -1) {
                acc.push(current);
            } else {
                acc[existingIndex] = current;
            }
            return acc;
        }, []);
    }, [applyPropertyDetailsQuestions, updatePropertyDetailsQuestions, renewPropertyDetailsQuestions]);

    const { propertyDetails, property } = useSelector(selectRenewalPermitData);

    const [formValues, setFormValues] = useState({})
    const [formError, setFormError] = useState({})

    useEffect(() => {
        setFormValues({ ...propertyDetails, ...property });
    }, [propertyDetails, property])


    const formModel = useMemo(() => ({ ...formValues }), [formValues])

    const onRadioQuestionGroupChanged = (value, _, questionKey) => {
        updateFormValues(questionKey, value)
    }

    const updateFormValues = (property, value) => {
        const currentValues = { ...formValues }
        currentValues[property] = value
        setFormValues(currentValues)
    }

    const prepareSupplementalQuestionsObject = () => {
        let supplementalQuestions = {};
        for (const question of allPropertyDetailsQuestions) {
            supplementalQuestions[question.key] = {
                question: question.title,
                questionSubtext: question.description,
                answer: formValues[question.key] || null, // api call expects there to be an answer
                ref: question.key
            }
        }
        return supplementalQuestions;
    }

    const updateApplicationData = () => {
        const supplementalQuestions = prepareSupplementalQuestionsObject();
        dispatch(
            updatePermitData({
                propertyDetails: {
                    ...formValues,
                    supplementalQuestions: {
                        ...supplementalQuestions
                    }
                }
            })
        )
    }

    const dispatchPropertyDetails = () => {
        if (!form?.current?.check()) return false
        updateApplicationData();
        return true;
    }

    const handleBackClick = () => {
        // don't validate on back click
        // it will be validated next time they go next on this page
        updateApplicationData();
        return true;
    }
    return (
        <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={24}>
                <PortalHeader flow={FLOWS_ENUM.RENEW_LICENSE} />
            </FlexboxGrid.Item>
            <FlexboxGrid.Item colspan={24}>
                <Content>
                    <FlowPageLayout
                        title="Property Details"
                        onNext={dispatchPropertyDetails}
                        onBack={handleBackClick}
                    >
                        <Grid fluid>
                            <Row>
                                <Col>
                                    <Text className="mb-24">All fields must be completed before proceeding.</Text>
                                </Col>
                            </Row>

                            <Row className="mt-12 mb-24">
                                <Col xs={24}>
                                    <Form
                                        ref={form}
                                        checkTrigger="none"
                                        formDefaultValue={formModel}
                                        formValue={formValues}
                                        onCheck={setFormError}
                                        onChange={setFormValues}
                                        fluid
                                    >
                                        <div role="grid">
                                            {renewPropertyDetailsQuestions?.map((question, idx) => {
                                                return (
                                                    <Row
                                                        className="mb-24"
                                                        key={`question_${convertStringToKey(question.key)}_${idx}`}
                                                    >
                                                        <Col xs={24}>{mapQuestionToComponent(question, formValues, formError, updateFormValues, onRadioQuestionGroupChanged)}</Col>
                                                    </Row>
                                                )
                                            })}
                                        </div>
                                    </Form>
                                </Col>
                            </Row>
                        </Grid>
                    </FlowPageLayout>
                </Content>
            </FlexboxGrid.Item>
        </FlexboxGrid>
    )
}
export default RenewPropertyDetails
